/**
 * useGroupedEvents — Iter-310 — Group shift rows by event_group_id.
 *
 * The API returns one row per role-slot. The user-facing UI (Client
 * Dashboard, Admin Shifts list, Calendar) should render ONE card per
 * event with all of that event's role slots nested underneath. This
 * hook does the grouping in JavaScript so we don't have to touch any
 * existing backend list endpoint contracts beyond adding the two
 * `event_group_id` + `event_title` fields.
 *
 * Contract:
 *   Input  — `rows`: array of shift objects. Each must have at least
 *            `id`, `event_group_id`, `shift_date`. Anything else is
 *            preserved on each role inside the group.
 *   Output — array of:
 *     {
 *       isGroup: true,
 *       key:      'group::<event_group_id>',
 *       event_group_id,
 *       event_title,
 *       shift_date, end_date, start_time, end_time,
 *       venue_name, venue_address, city, state,
 *       client_display, client_id, client_logo_url,
 *       roles: [<full shift row>, ...],   // 2+ roles
 *       roles_count, total_talent_needed, total_confirmed, total_pending,
 *       primary_shift_id,                 // earliest shift_id — for "open Event Overview" links
 *     }
 *     | {
 *       isGroup: false,
 *       key:      'shift::<id>',
 *       ...originalShiftFields,
 *     }
 *
 * Stable ordering: groups (and singleton rows) come back in the order
 * the FIRST representative of each group appeared in the input array,
 * so any sort applied upstream (newest, urgency, etc.) is preserved.
 *
 * IMPORTANT — Grouping is display-only. The per-role `id` is preserved
 * inside `roles[]` so applications, invitations, bookings, backups,
 * waitlists, and pending applications remain attached to the original
 * shift_id. Nothing about ownership/identity changes.
 */
import { useMemo } from 'react';

const _coerceNum = (v) => (typeof v === 'number' ? v : Number(v) || 0);
const _firstNonNull = (rows, key) => rows.find((r) => r[key] != null && r[key] !== '')?.[key];

export function groupShiftsByEvent(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  // First pass: count how many rows share each event_group_id so we can
  // decide whether a row is part of a true multi-role group vs a
  // singleton (singletons render exactly as today — no parent shell).
  const counts = new Map();
  for (const r of rows) {
    const g = r?.event_group_id;
    if (!g) continue;
    counts.set(g, (counts.get(g) || 0) + 1);
  }

  // Second pass: walk rows in order, emitting either a group (once per
  // event_group_id when count >= 2) or a singleton.
  const seenGroups = new Set();
  const out = [];

  for (const r of rows) {
    const g = r?.event_group_id;
    const inMultiRoleGroup = !!g && (counts.get(g) || 0) >= 2;

    if (!inMultiRoleGroup) {
      out.push({ isGroup: false, key: `shift::${r.id}`, ...r });
      continue;
    }
    if (seenGroups.has(g)) continue;
    seenGroups.add(g);

    const members = rows.filter((x) => x?.event_group_id === g);

    // Sort role members by role_name so "Bartender, Brand Ambassador,
    // Captain" reads stably across page loads.
    members.sort((a, b) => String(a.role_name || '').localeCompare(String(b.role_name || '')));

    const total_talent_needed = members.reduce((s, m) => s + _coerceNum(m.talent_needed), 0);
    const total_confirmed = members.reduce(
      (s, m) => s + _coerceNum(m.confirmed_count ?? m.staffed_count ?? m.bookings_count),
      0,
    );
    const total_pending = members.reduce(
      (s, m) => s + _coerceNum(m.applicant_count ?? m.pending_applications),
      0,
    );

    out.push({
      isGroup: true,
      key: `group::${g}`,
      event_group_id: g,
      event_title: _firstNonNull(members, 'event_title'),
      shift_date: _firstNonNull(members, 'shift_date'),
      end_date: _firstNonNull(members, 'end_date'),
      start_time: _firstNonNull(members, 'start_time'),
      end_time: _firstNonNull(members, 'end_time'),
      venue_name: _firstNonNull(members, 'venue_name'),
      venue_address: _firstNonNull(members, 'venue_address'),
      city: _firstNonNull(members, 'city'),
      state: _firstNonNull(members, 'state'),
      client_id: _firstNonNull(members, 'client_id'),
      client_display: _firstNonNull(members, 'client_display'),
      client_logo_url: _firstNonNull(members, 'client_logo_url'),
      status: _firstNonNull(members, 'status'),
      series_id: _firstNonNull(members, 'series_id'),
      is_multi_day: members.some((m) => m.is_multi_day),
      series_day_index: _firstNonNull(members, 'series_day_index'),
      series_total: _firstNonNull(members, 'series_total'),
      series_dates: _firstNonNull(members, 'series_dates'),
      roles: members,
      roles_count: members.length,
      total_talent_needed,
      total_confirmed,
      total_pending,
      primary_shift_id: members[0]?.id,
    });
  }

  return out;
}

/** Hook wrapper — memoizes the grouping so React lists are stable. */
export function useGroupedEvents(rows) {
  return useMemo(() => groupShiftsByEvent(rows), [rows]);
}

export default useGroupedEvents;
